import { UserContext } from "context/UserContext";
import { ReactNode, useContext, useMemo, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, useFormContext, useWatch } from "react-hook-form";
import { FaPlus } from "react-icons/fa6";
import { SingleValue } from "react-select";
import { api } from "../../../constants/RotasAPI";
import { useGetCondominioPossuiContaProvisionamentoAtiva } from "hooks/condominio/useGetCondominioPossuiContaProvisionamentoAtiva";
import CirculoSinalNegativo from "shared/components/circuloSinalNegativo/CirculoSinalNegatvo";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { MesCompetencia } from "shared/components/date/MesCompetencia";
import CustomNumberInput from "shared/components/input/CustomNumberInput";
import TextInput from "shared/components/input/TextInput";
import SelectAsync from "shared/components/select/SelectAsync";
import showAlert from "shared/util/functions/alert/showAlert";
import { criarSelectOptions } from "shared/util/functions/selectOptions/SelectOptionsFunctions";
import { RegistroFornecedorSelectAsyncDTO } from "types/dto/fornecedor/RegistroFornecedorSelectAsyncDTO";
import { CadastrarDespesaUnicaGroupPayForm } from "types/reactHookForm/formTypes/CadastrarDespesaUnicaGroupPayForm";
import { SelectOptionType } from "types/select/SelectOptionType";
import CustomSelect from "shared/components/select/CustomSelect";
import { ClassePlanoContasYupSchemaDTO } from "types/dto/contasPagar/ClassePlanoContasYupSchemaDTO";
import { useGetProximoChequeDisponivel } from "hooks/groupPay/useGetProximoChequeDisponivel";
import { formatarValorMonetario } from "shared/util/functions/formatacoesDeValores/FormatarValores";

interface SecaoDadosDespesaModalCadastrarDespesaUnicaGroupPayProps {
  classesConta: FieldArrayWithId<CadastrarDespesaUnicaGroupPayForm, "classesConta">[];
  adicionarClasseConta: UseFieldArrayAppend<CadastrarDespesaUnicaGroupPayForm, "classesConta">;
  removerClasseConta: UseFieldArrayRemove;
  valorBrutoTotal: number;
  condominioId: number;
}

export const TODAS_OPCOES_PAGAMENTO: SelectOptionType<string>[] = [
  { label: "Cartão de crédito", value: "CARTAO_CREDITO" },
  { label: "Cartão de débito", value: "CARTAO_DEBITO" },
  { label: "Cheque", value: "CHEQUE" },
  { label: "Débito automático", value: "DEBITO_AUTOMATICO" },
  { label: "Débito em conta", value: "DEBITO_CONTA" },
  { label: "Dinheiro", value: "DINHEIRO" },
  { label: "Boleto", value: "BOLETO" },
  { label: "DOC", value: "DOC" },
  { label: "TED", value: "TED" },
  { label: "Transferência bancária", value: "TRANSFERENCIA_BANCARIA" },
  { label: "PIX", value: "PIX" },
];

export const OPCOES_PERMITIDAS_INVESTIMENTO = ["DEBITO_CONTA", "DINHEIRO", "BOLETO", "TED", "PIX"];

export const getOpcoesPagamento = (contaSelecionada: any) => {
  if (!contaSelecionada) {
    return TODAS_OPCOES_PAGAMENTO;
  }

  if (contaSelecionada.contaCaixinha) {
    return [{ label: "Dinheiro", value: "DINHEIRO" }];
  }

  const tipoConta = contaSelecionada.contaBancaria?.tipoContaBancaria;

  if (tipoConta === "CONTA_INVESTIMENTO") {
    return TODAS_OPCOES_PAGAMENTO.filter((opcao) => OPCOES_PERMITIDAS_INVESTIMENTO.includes(opcao.value));
  }

  return TODAS_OPCOES_PAGAMENTO;
};

const SecaoDadosDespesaModalCadastrarDespesaUnicaGroupPay: React.FC<
  SecaoDadosDespesaModalCadastrarDespesaUnicaGroupPayProps
> = (props) => {
  const { register, control, setValue, clearErrors, watch } = useFormContext<CadastrarDespesaUnicaGroupPayForm>();
  const { condominioId } = props;
  const conta = watch("conta");
  const tipoFormaPagamento = useWatch({ name: "tipoFormaPagamento" });

  const [formaPagamentoPix, setFormaPagamentoPix] = useState<string | null>(null);
  const [informarManualmente, setInformarManualmente] = useState(false);
  const [isFavorecidoFornecedor, setIsFavorecidoFornecedor] = useState(false);
  const codigoDeBarrasPreenchido = useWatch({ control, name: "codigoDeBarras" });

  useEffect(() => {
    if (codigoDeBarrasPreenchido?.trim()) {
      setInformarManualmente(true);
    }
  }, [codigoDeBarrasPreenchido]);

  const { data: dataProvisionamento } = useGetCondominioPossuiContaProvisionamentoAtiva(Number(condominioId));
  const possuiContaProvisionamento = dataProvisionamento?.payload ?? false;
  const { competenciaSelecionada } = useContext(UserContext);

  const opcoesFormaPagamento = useMemo(() => getOpcoesPagamento(conta?.value), [conta?.value]);

  useEffect(() => {
    setValue("tipoFormaPagamento", null);
    setValue("tipoFormaPagamentoPix", null, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setValue("codigoCopiaColaPix", "", { shouldDirty: true });
    setFormaPagamentoPix(null);
  }, [conta?.value?.id, setValue]);

  const handleChangeFormaPagamento = (opcaoSelecionada: SingleValue<SelectOptionType<string>>) => {
    setValue("tipoFormaPagamento", opcaoSelecionada);

    if (opcaoSelecionada?.value === "PIX") {
      setValue("tipoFormaPagamentoPix", "TRANSFERENCIA", { shouldDirty: true });
    } else {
      setValue("tipoFormaPagamentoPix", null, { shouldDirty: true });
      setValue("codigoCopiaColaPix", "", { shouldDirty: true });
    }
  };

  const handleChangeFormaPagamentoPix = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    setFormaPagamentoPix(valor);
    if (valor !== "QR_CODE") {
      setValue("codigoCopiaColaPix", "");
    }
  };

  const handleFornecedorChange = (
    opcaoSelecionada: SingleValue<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>
  ) => {
    clearErrors("fornecedor");

    if (opcaoSelecionada?.value?.detalhesClassePlanoContas?.idClassePlano) {
      setValue(
        "classesConta.0.classePlano",
        criarSelectOptions(
          { idClassePlano: opcaoSelecionada.value.detalhesClassePlanoContas.idClassePlano },
          opcaoSelecionada.value.detalhesClassePlanoContas.classePlanoContasNome
        )
      );
      setValue("classesConta.0.descricao", opcaoSelecionada.value.descricaoClassePlanoFornecedor);
      clearErrors("classesConta.0.classePlano");

      if (opcaoSelecionada.value.detalhesClassePlanoContas.idContaProvisionamentoVinculada) {
        setValue(
          "classesConta.0.contaProvisionamento",
          criarSelectOptions(
            opcaoSelecionada.value.detalhesClassePlanoContas.idContaProvisionamentoVinculada,
            opcaoSelecionada.value.detalhesClassePlanoContas.nomeContaProvisionamentoVinculada
          )
        );
        clearErrors("classesConta.0.contaProvisionamento");
      }
    } else {
      setValue("classesConta.0.classePlano", null);
      setValue("classesConta.0.descricao", null);
      setValue("classesConta.0.contaProvisionamento", null);
      clearErrors("classesConta.0.classePlano");
    }

    if (opcaoSelecionada?.value?.naturezaRendimento) {
      setValue(
        "naturezaRendimento",
        criarSelectOptions(
          opcaoSelecionada.value.naturezaRendimento.id,
          opcaoSelecionada.value.naturezaRendimento.codigoDescricao
        )
      );
    } else {
      setValue("naturezaRendimento", null);
    }
  };

  const handleClasseContaChange = (
    opcaoSelecionada: SingleValue<SelectOptionType<ClassePlanoContasYupSchemaDTO>>,
    index: number
  ) => {
    clearErrors(`classesConta.${index}.classePlano`);

    if (opcaoSelecionada?.value.idContaProvisionamentoVinculada) {
      setValue(
        `classesConta.${index}.contaProvisionamento`,
        criarSelectOptions(
          opcaoSelecionada.value.idContaProvisionamentoVinculada,
          opcaoSelecionada.value.nomeContaProvisionamentoVinculada
        )
      );
      clearErrors(`classesConta.${index}.contaProvisionamento`);
    }
  };

  const renderClasseContaValorBruto = (): ReactNode => {
    if (props.classesConta.length > 0) {
      let sizeDescricao = possuiContaProvisionamento ? 3 : 6;
      return props.classesConta.map((_classeConta: unknown, index: number) => {
        return (
          <Row key={`classeContaValorBruto-${index}`} className="mb-2 align-items-center">
            <Col xs={3}>
              <SelectAsync
                key={`select-classe-conta-${index}`}
                id={`select-classe-conta-${index}`}
                label="Classe de conta (Categoria) *"
                endpoint={`/api/plano-contas/classe-plano/buscar-registros-select-async?condominioId=${condominioId}&tipoGrupo=2`}
                atributosRefresh={[condominioId]}
                nomeAtributoValue="value"
                nomeAtributoLabel="descricao"
                usarValidacaoParaFormulario
                control={control}
                register={register}
                setValue={setValue}
                name={`classesConta.${index}.classePlano`}
                onChange={(opcaoSelecionada) => handleClasseContaChange(opcaoSelecionada, index)}
                isDisabled={condominioId === null}
                isClearable={true}
              />
            </Col>
            <Col xs={2}>
              <CustomNumberInput
                key={`valor-bruto-${index}`}
                id={`valor-bruto-${index}`}
                label="Valor bruto (R$) *"
                prefix="R$ "
                placeholder="R$ 0,00"
                decimalScale={2}
                usarValidacaoParaFormulario
                control={control}
                register={register}
                setValue={setValue}
                fixedDecimalScale={true}
                name={`classesConta.${index}.valorBruto`}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return void 0 === floatValue || (floatValue > 0 && floatValue <= 999999999999.99);
                }}
              />
            </Col>
            <Col xs={sizeDescricao}>
              <TextInput
                usarValidacaoParaFormulario={true}
                id={`descricao-${index}`}
                label="Descrição"
                name={`classesConta.${index}.descricao`}
                register={register}
                control={control}
                setValue={setValue}
              />
            </Col>
            {possuiContaProvisionamento && (
              <Col xs={3}>
                <SelectAsync
                  id={`select-conta-provisionamento-${index}`}
                  label="Conta de provisionamento *"
                  endpoint={`/api/conta-provisionamento/buscar-registros-select-async-cadastro-despesa/${condominioId}`}
                  atributosRefresh={[condominioId]}
                  nomeAtributoValue="id"
                  nomeAtributoLabel="nomeConta"
                  usarValidacaoParaFormulario
                  name={`classesConta.${index}.contaProvisionamento`}
                  control={control}
                  register={register}
                  setValue={setValue}
                  onChange={() => clearErrors(`classesConta.${index}.contaProvisionamento`)}
                  isClearable={true}
                />
              </Col>
            )}
            {index !== 0 && (
              <Col xs={1} className="d-flex align-items-center justify-content-center pt-3">
                <CirculoSinalNegativo onClick={() => props.removerClasseConta(index)} size={16} />
              </Col>
            )}
          </Row>
        );
      });
    }
    return null;
  };

  const adicionarClasseConta = () => {
    if (props.classesConta.length === 1) {
      showAlert({
        texto: "Ao adicionar mais de uma classe de conta, não será possível informar retenção. Deseja continuar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => {
          setValue("retencoes", []);
          props.adicionarClasseConta({
            classePlano: { label: "", value: null },
            valorBruto: undefined,
            contaProvisionamento: null,
            condominioPossuiContaProvisionamentoAtiva: possuiContaProvisionamento,
          });
        },
      });
    } else {
      setValue("retencoes", []);
      props.adicionarClasseConta({
        classePlano: { label: "", value: null },
        valorBruto: undefined,
        contaProvisionamento: null,
        condominioPossuiContaProvisionamentoAtiva: possuiContaProvisionamento,
      });
    }
  };

  const { data: numeroProximoCheque } = useGetProximoChequeDisponivel(conta?.value?.contaBancaria?.id);

  useEffect(() => {
    if (tipoFormaPagamento?.value === "CHEQUE" && numeroProximoCheque?.numero) {
      setValue("numeroCheque", numeroProximoCheque.numero.toString());
    } else {
      setValue("numeroCheque", "");
    }
  }, [tipoFormaPagamento, numeroProximoCheque]);

  return (
    <>
      <Row>
        <Col xs={8}>
          <SelectAsync
            usarValidacaoParaFormulario={true}
            id="condominio"
            name="condominio"
            label="Condomínio"
            endpoint="/api/condominio/registros"
            nomeAtributoValue="id"
            nomeAtributoLabel="nomeFantasia"
            control={control}
            register={register}
            setValue={setValue}
            isDisabled
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={3}>
          <MesCompetencia
            name="mesCompetencia"
            label="Mês competência *"
            defaultValue={competenciaSelecionada}
            control={control}
            setValue={setValue}
            register={register}
            usarValidacaoParaFormulario
          />
        </Col>
      </Row>

      <Row className="mt-4 align-items-end">
        <Col xs={8}>
          <SelectAsync
            id="select-fornecedor"
            name="fornecedor"
            label={isFavorecidoFornecedor ? "Fornecedor *" : "Fornecedor/Favorecido *"}
            control={control}
            register={register}
            setValue={setValue}
            endpoint={`/api/fornecedor/buscar-registros-select-async?condominioId=${condominioId}`}
            nomeAtributoLabel="nome"
            nomeAtributoValue="value"
            usarValidacaoParaFormulario
            onChange={handleFornecedorChange}
            isClearable={true}
          />
        </Col>

        <Col xs={3} className="mb-3">
          <Form.Group controlId="isFavorecidoFornecedorCheckbox">
            <Form.Check
              type="checkbox"
              label="Favorecido não é o fornecedor"
              checked={isFavorecidoFornecedor}
              onChange={(e) => {
                setIsFavorecidoFornecedor(e.target.checked);
                setValue("isFavorecidoFornecedor", e.target.checked);
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      {isFavorecidoFornecedor && (
        <Row className="mt-4">
          <Col xs={9}>
            <SelectAsync
              id="select-favorecido"
              name="favorecido"
              label="Favorecido *"
              endpoint={`/api/fornecedor/buscar-registros-select-async?condominioId=${condominioId}`}
              nomeAtributoLabel="nome"
              nomeAtributoValue="value"
              usarValidacaoParaFormulario
              control={control}
              register={register}
              setValue={setValue}
              isClearable={true}
            />
          </Col>
        </Row>
      )}

      <div className="mt-4">{renderClasseContaValorBruto()}</div>

      <Row className="mt-4">
        <Col onClick={adicionarClasseConta} className="botao-adicionar-item-lista" xs={3}>
          <FaPlus size={15} />
          <span className="ms-2">Adicionar classe de conta</span>
        </Col>
        <Col>
          {props.classesConta.length > 1 && (
            <span className="fw-bold" style={{ color: "#676A6C" }}>
              R$ {formatarValorMonetario(props.valorBrutoTotal)}
            </span>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={3}>
          <CustomDatePicker
            label="Data de vencimento *"
            name="dataVencimento"
            value={watch("dataVencimento")}
            control={control}
            setValue={setValue}
            register={register}
            usarValidacaoParaFormulario
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={3}>
          <TextInput
            usarValidacaoParaFormulario={true}
            id="numeroDocumento"
            label="Número do documento"
            name="numeroDocumento"
            control={control}
            setValue={setValue}
            register={register}
          />
        </Col>
        <Col xs={2}>
          <TextInput
            usarValidacaoParaFormulario={true}
            id="serieNf"
            label="Série da NF"
            name="serieNf"
            control={control}
            setValue={setValue}
            register={register}
          />
        </Col>
        <Col xs={3}>
          <CustomDatePicker
            label="Data de emissão"
            name="dataEmissao"
            value={watch("dataEmissao") ?? undefined}
            control={control}
            setValue={setValue}
            register={register}
            usarValidacaoParaFormulario
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={3}>
          <SelectAsync
            id="contaId"
            label="Conta"
            name="conta"
            nomeAtributoValue="conta"
            nomeAtributoLabel="conta.descricao"
            endpoint={`${api.conta.getContasByCondominio}/${condominioId}?ativo=true`}
            atributosRefresh={[condominioId]}
            control={control}
            setValue={setValue}
            register={register}
            usarValidacaoParaFormulario
            isDisabled={condominioId === null}
            isClearable={true}
          />
        </Col>
        <Col xs={8}>
          <CustomSelect
            id="forma-pagamento"
            label="Forma de Pagamento"
            usarValidacaoParaFormulario
            name="tipoFormaPagamento"
            options={opcoesFormaPagamento}
            control={control}
            register={register}
            setValue={setValue}
            onChange={handleChangeFormaPagamento}
            isClearable={true}
          />
        </Col>
      </Row>

      {tipoFormaPagamento?.value === "PIX" && (
        <Row className="mt-4 align-items-end">
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Forma de Pagamento PIX</Form.Label>
              <div className="d-flex pt-2">
                <Form.Check
                  inline
                  label="Transferência"
                  type="radio"
                  value="TRANSFERENCIA"
                  {...register("tipoFormaPagamentoPix")}
                  onChange={handleChangeFormaPagamentoPix}
                />
                <Form.Check
                  inline
                  label="QR Code"
                  type="radio"
                  value="QR_CODE"
                  {...register("tipoFormaPagamentoPix")}
                  onChange={handleChangeFormaPagamentoPix}
                />
              </div>
            </Form.Group>
          </Col>

          {formaPagamentoPix === "QR_CODE" && (
            <Col xs={7}>
              <TextInput
                usarValidacaoParaFormulario={true}
                id="codigoCopiaColaPix"
                label="Código Copia e Cola PIX"
                name="codigoCopiaColaPix"
                control={control}
                setValue={setValue}
                register={register}
              />
            </Col>
          )}
        </Row>
      )}

      <Row className="mt-4 align-items-end">
        <Col xs={8}>
          <TextInput
            usarValidacaoParaFormulario={true}
            id="codigoDeBarras"
            label={informarManualmente ? "Linha digitável" : "Código de barras"}
            placeholder={informarManualmente ? "Informe a linha digitável" : "Aguardando a leitura do código de barras"}
            name="codigoDeBarras"
            control={control}
            setValue={setValue}
            register={register}
            readOnly={!informarManualmente}
          />
        </Col>
        <Col xs={3}>
          <Form.Group controlId="informarManualmenteCheckbox">
            <Form.Check
              type="checkbox"
              label="Informar manualmente"
              checked={informarManualmente}
              onChange={() => {
                setValue("codigoDeBarras", null, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setInformarManualmente(!informarManualmente);
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={11}>
          <Form.Group controlId="observacaoTextarea">
            <Form.Label>Observação</Form.Label>
            <Form.Control as="textarea" rows={3} {...register("observacao")} />
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default SecaoDadosDespesaModalCadastrarDespesaUnicaGroupPay;
